import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  Sequence,
  Img,
} from 'remotion';

// 场景1：Logo展示
const Scene1: React.FC = () => {
  const frame = useCurrentFrame();
  const logoScale = interpolate(frame, [0, 20], [0, 1], { extrapolateRight: 'clamp' });
  const nameOpacity = interpolate(frame, [20, 40], [0, 1], { extrapolateRight: 'clamp' });
  const sloganOpacity = interpolate(frame, [40, 60], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{
      background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 40,
    }}>
      <div style={{
        width: 200, height: 200, background: '#ffffff', borderRadius: 30,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        transform: `scale(${logoScale})`, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <span style={{ fontSize: 100, fontFamily: 'Arial Black, sans-serif', color: '#1e3c72' }}>P</span>
      </div>
      <h1 style={{
        fontFamily: 'Arial Black, sans-serif', fontSize: 120, fontWeight: 900, color: '#ffffff',
        textShadow: '4px 4px 0px rgba(0,0,0,0.3)', opacity: nameOpacity, letterSpacing: 10,
      }}>
        PRO TECH
      </h1>
      <h2 style={{
        fontFamily: 'Arial, sans-serif', fontSize: 50, fontWeight: 400, color: '#ffffff',
        textShadow: '2px 2px 0px rgba(0,0,0,0.3)', opacity: sloganOpacity, letterSpacing: 8,
      }}>
        Innovation Drives Future
      </h2>
    </AbsoluteFill>
  );
};

// 场景2：核心业务
const Scene2: React.FC = () => {
  const frame = useCurrentFrame();

  const businesses = [
    { icon: '💻', title: '软件开发', desc: 'Enterprise Solutions' },
    { icon: '☁️', title: '云服务', desc: 'Cloud Computing' },
    { icon: '🔒', title: '网络安全', desc: 'Cyber Security' },
    { icon: '🤖', title: 'AI解决方案', desc: 'AI Solutions' },
  ];

  return (
    <AbsoluteFill style={{
      background: '#f5f7fa',
      padding: 60,
    }}>
      <h1 style={{
        fontFamily: 'Arial Black, sans-serif', fontSize: 70, fontWeight: 900, color: '#1e3c72',
        textAlign: 'center', marginBottom: 60, letterSpacing: 4,
      }}>
        核心业务
      </h1>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 30,
      }}>
        {businesses.map((b, i) => {
          const delay = i * 8;
          const adjustedFrame = Math.max(0, frame - delay);
          const scale = adjustedFrame < 10 ? interpolate(adjustedFrame, [0, 10], [0.8, 1]) : 1;
          const opacity = adjustedFrame < 10 ? interpolate(adjustedFrame, [0, 10], [0, 1]) : 1;

          return (
            <div key={i} style={{
              background: '#ffffff', borderRadius: 20, padding: 40,
              display: 'flex', alignItems: 'center', gap: 30,
              transform: `scale(${scale})`, opacity,
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            }}>
              <div style={{ fontSize: 80 }}>{b.icon}</div>
              <div>
                <h2 style={{ fontFamily: 'Arial Black, sans-serif', fontSize: 45, color: '#1e3c72', margin: 0 }}>
                  {b.title}
                </h2>
                <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 30, color: '#666666', margin: 0, marginTop: 10 }}>
                  {b.desc}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// 场景3：数据展示
const Scene3: React.FC = () => {
  const frame = useCurrentFrame();

  const stats = [
    { value: '500+', label: '企业客户' },
    { value: '99.9%', label: '系统可用性' },
    { value: '24/7', label: '技术支持' },
    { value: '50+', label: '国家覆盖' },
  ];

  return (
    <AbsoluteFill style={{
      background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 60, padding: 60,
    }}>
      <h1 style={{
        fontFamily: 'Arial Black, sans-serif', fontSize: 70, fontWeight: 900, color: '#ffffff',
        textAlign: 'center', letterSpacing: 4,
      }}>
        企业实力
      </h1>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 40, width: '100%',
      }}>
        {stats.map((s, i) => {
          const delay = i * 8;
          const adjustedFrame = Math.max(0, frame - delay);
          const scale = adjustedFrame < 10 ? interpolate(adjustedFrame, [0, 10], [0.8, 1]) : 1;
          const opacity = adjustedFrame < 10 ? interpolate(adjustedFrame, [0, 10], [0, 1]) : 1;

          return (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.1)', borderRadius: 20, padding: 40,
              display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
              transform: `scale(${scale})`, opacity,
            }}>
              <h1 style={{
                fontFamily: 'Arial Black, sans-serif', fontSize: 80, fontWeight: 900, color: '#ffffff',
                margin: 0, letterSpacing: 2,
              }}>
                {s.value}
              </h1>
              <p style={{
                fontFamily: 'Arial, sans-serif', fontSize: 35, color: '#ffffff', margin: 0, marginTop: 20,
              }}>
                {s.label}
              </p>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// 场景4：联系方式
const Scene4: React.FC = () => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, 10], [0.8, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{
      background: '#ffffff',
      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 40, padding: 60,
    }}>
      <div style={{
        width: 150, height: 150, background: '#1e3c72', borderRadius: 20,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        transform: `scale(${scale})`, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <span style={{ fontSize: 80, fontFamily: 'Arial Black, sans-serif', color: '#ffffff' }}>P</span>
      </div>
      <h1 style={{
        fontFamily: 'Arial Black, sans-serif', fontSize: 80, fontWeight: 900, color: '#1e3c72',
        letterSpacing: 8,
      }}>
        PRO TECH
      </h1>
      <div style={{
        display: 'flex', gap: 60, marginTop: 20,
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 35, color: '#666666', margin: 0 }}>
            官网
          </p>
          <p style={{ fontFamily: 'Arial Black, sans-serif', fontSize: 40, color: '#1e3c72', margin: 0, marginTop: 10 }}>
            www.protech.com
          </p>
        </div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 35, color: '#666666', margin: 0 }}>
            电话
          </p>
          <p style={{ fontFamily: 'Arial Black, sans-serif', fontSize: 40, color: '#1e3c72', margin: 0, marginTop: 10 }}>
            400-123-4567
          </p>
        </div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 35, color: '#666666', margin: 0 }}>
            邮箱
          </p>
          <p style={{ fontFamily: 'Arial Black, sans-serif', fontSize: 40, color: '#1e3c72', margin: 0, marginTop: 10 }}>
            info@protech.com
          </p>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// 主组件
export const EnterpriseBrandingTemplate: React.FC = () => {
  const { fps } = useVideoConfig();

  return (
    <AbsoluteFill>
      <Sequence from={0} durationInFrames={fps * 2.5}>
        <Scene1 />
      </Sequence>
      <Sequence from={fps * 2.5} durationInFrames={fps * 3}>
        <Scene2 />
      </Sequence>
      <Sequence from={fps * 5.5} durationInFrames={fps * 2.5}>
        <Scene3 />
      </Sequence>
      <Sequence from={fps * 8} durationInFrames={fps * 2}>
        <Scene4 />
      </Sequence>
    </AbsoluteFill>
  );
};
